"use client";

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import type { CountryCode } from "libphonenumber-js/min";
import { useCustomerAuth } from "@/context/auth-context";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";
import { FormField, PhoneField, AddressFields } from "@/components/checkout/form-fields";
import { CountryCombobox } from "@/components/checkout/country-combobox";
import { getCountryOptions } from "@/lib/countries";
import { isValidPhone, toE164 } from "@/lib/phone";

const ease = [0.16, 1, 0.3, 1] as const;

type FieldKey =
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "password"
  | "confirmPassword"
  | "postalCode"
  | "houseNumber"
  | "street"
  | "city"
  | "dateOfBirth";

export function RegisterPage() {
  const { register } = useCustomerAuth();
  const t = useTranslations("account");
  const locale = useLocale();

  // Identity
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");

  // Contact
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState<CountryCode>("NL");

  // Address — optional at register; required only at first checkout
  const [country, setCountry] = useState<CountryCode>("NL");
  const [postalCode, setPostalCode] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [addition, setAddition] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");

  // Newsletter — single combined checkbox, default checked per spec
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);

  // Lifecycle
  const [submitting, setSubmitting] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const markTouched = (k: FieldKey) =>
    setTouched((prev) => (prev[k] ? prev : { ...prev, [k]: true }));

  const countryOptions = useMemo(() => getCountryOptions(locale), [locale]);

  // Geolocate the phone country once on mount via Cloudflare-set header.
  // Cheap (no extra service), reasonably accurate, falls back to NL when
  // the header isn't present (local dev).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/geo/country");
        if (!res.ok) return;
        const data = (await res.json()) as { country?: string };
        const code = (data.country || "").toUpperCase();
        if (!cancelled && code && countryOptions.some((o) => o.code === code)) {
          setPhoneCountry(code as CountryCode);
          setCountry(code as CountryCode);
        }
      } catch {
        // Geo is a nicety, not a requirement.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countryOptions]);

  // ── Validation ─────────────────────────────────────────────────────
  // Phone is required. Address is optional, but if any field is filled
  // ALL of postalCode/houseNumber/street/city become required so we don't
  // persist a half-filled row. Email format check is HTML5; the browser
  // also blocks submit on type=email mismatch, but we also do a regex
  // fallback so the disabled-button state is responsive.
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const phoneValid = isValidPhone(phoneNumber, phoneCountry);
  // Birthdate is required so the backend birthday-email cron has a date to
  // match against. Sanity-check it's a real past date with a plausible age;
  // <16 likely means a typo (16 is the Dutch digital-consent threshold).
  const dobValue = dateOfBirth.trim();
  const dobDate = dobValue ? new Date(dobValue) : null;
  const today = new Date();
  const minAdultDate = new Date(today.getFullYear() - 16, today.getMonth(), today.getDate());
  const dobValid = Boolean(
    dobDate &&
      !Number.isNaN(dobDate.getTime()) &&
      dobDate <= minAdultDate &&
      dobDate.getFullYear() >= 1900,
  );
  const anyAddressFilled = Boolean(
    postalCode.trim() || houseNumber.trim() || street.trim() || city.trim(),
  );
  const addressComplete =
    Boolean(postalCode.trim() && houseNumber.trim() && street.trim() && city.trim());
  const addressOk = !anyAddressFilled || addressComplete;

  const errors: Partial<Record<FieldKey, string>> = {};
  if (touched.firstName && !firstName.trim()) errors.firstName = t("firstNameRequired");
  if (touched.lastName && !lastName.trim()) errors.lastName = t("lastNameRequired");
  if (touched.email && !emailLooksValid) errors.email = t("emailInvalid");
  if (touched.phone && !phoneValid) errors.phone = t("phoneInvalid");
  if (touched.password && password.length < 8) errors.password = t("passwordTooShort");
  if (touched.confirmPassword && confirmPassword !== password)
    errors.confirmPassword = t("passwordMismatch");
  if (touched.dateOfBirth && !dobValid)
    errors.dateOfBirth = t("dateOfBirthInvalid");
  if (anyAddressFilled) {
    if (touched.postalCode && !postalCode.trim()) errors.postalCode = t("required");
    if (touched.houseNumber && !houseNumber.trim()) errors.houseNumber = t("required");
    if (touched.street && !street.trim()) errors.street = t("required");
    if (touched.city && !city.trim()) errors.city = t("required");
  }

  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    emailLooksValid &&
    phoneValid &&
    dobValid &&
    password.length >= 8 &&
    confirmPassword === password &&
    addressOk;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setError("");
    setSubmitting(true);

    const e164 = toE164(phoneNumber, phoneCountry) || "";
    const payload = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      password,
      phone: e164,
      dateOfBirth,
      newsletterOptIn,
      locale,
      ...(addressComplete
        ? {
            country,
            postalCode: postalCode.trim(),
            houseNumber: houseNumber.trim(),
            addition: addition.trim() || undefined,
            street: street.trim(),
            city: city.trim(),
          }
        : {}),
    };

    const result = await register(payload);
    if (result.success) {
      setSubmittedEmail(email.trim());
    } else {
      setError(result.error || t("registerFailed"));
      setSubmitting(false);
    }
  }

  // After a successful submit we show a "check your inbox" panel rather
  // than redirecting straight to /account. The customer is already
  // logged in (the JWT cookie is set), but the welcome+verify email
  // landed in their inbox and we want to surface that explicitly so
  // they don't miss it. They can click "Continue to my account" at
  // their leisure.
  if (submittedEmail) {
    return <SubmittedNotice email={submittedEmail} />;
  }

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <span>/</span>
            <span className="text-text-secondary">{t("register")}</span>
          </nav>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
            className="max-w-xl mx-auto"
          >
            <div className="text-center mb-10">
              <p className="label-sm mb-3">{t("label")}</p>
              <h1 className="heading-md">{t("register")}</h1>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {/* Identity */}
              <section className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    label={t("firstNameField")}
                    name="firstName"
                    value={firstName}
                    onChange={setFirstName}
                    onBlur={() => markTouched("firstName")}
                    required
                    autoComplete="given-name"
                    error={errors.firstName}
                  />
                  <FormField
                    label={t("lastNameField")}
                    name="lastName"
                    value={lastName}
                    onChange={setLastName}
                    onBlur={() => markTouched("lastName")}
                    required
                    autoComplete="family-name"
                    error={errors.lastName}
                  />
                </div>

                <FormField
                  label={t("emailField")}
                  name="email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  onBlur={() => markTouched("email")}
                  required
                  autoComplete="email"
                  placeholder="je@email.nl"
                  error={errors.email}
                />

                <PhoneField
                  label={t("phoneLabel")}
                  hint={t("phoneHint")}
                  number={phoneNumber}
                  country={phoneCountry}
                  onNumberChange={setPhoneNumber}
                  onCountryChange={setPhoneCountry}
                  onBlur={() => markTouched("phone")}
                  options={countryOptions}
                  required
                  searchPlaceholder={t("countrySearchPlaceholder")}
                  noResultsLabel={t("countryNoResults")}
                  error={errors.phone}
                />

                <FormField
                  label={t("dateOfBirthLabel")}
                  hint={t("dateOfBirthHint")}
                  name="dateOfBirth"
                  type="date"
                  value={dateOfBirth}
                  onChange={setDateOfBirth}
                  onBlur={() => markTouched("dateOfBirth")}
                  autoComplete="bday"
                  required
                  error={errors.dateOfBirth}
                />
              </section>

              {/* Address — optional, single unified form (no NL/BE/world tabs) */}
              <section className="space-y-4">
                <div>
                  <h2 className="text-[15px] font-medium text-foreground">
                    {t("addressOptionalTitle")}
                  </h2>
                  <p className="text-[12px] text-text-tertiary mt-0.5">
                    {t("addressOptionalHint")}
                  </p>
                </div>
                <CountryCombobox
                  label={t("countryLabel")}
                  value={country}
                  onChange={(c) => {
                    setCountry(c);
                    if (!phoneNumber) setPhoneCountry(c);
                  }}
                  options={countryOptions}
                  placeholder={t("countrySearchPlaceholder")}
                  noResultsLabel={t("countryNoResults")}
                />
                <AddressFields
                  country={country}
                  postalCode={postalCode}
                  setPostalCode={setPostalCode}
                  houseNumber={houseNumber}
                  setHouseNumber={setHouseNumber}
                  addition={addition}
                  setAddition={setAddition}
                  street={street}
                  setStreet={setStreet}
                  city={city}
                  setCity={setCity}
                  errors={errors}
                  onBlur={(f) => markTouched(f)}
                  labels={{
                    postalCode: t("postalCodeLabel"),
                    houseNumber: t("houseNumberLabel"),
                    addition: t("additionLabel"),
                    additionPlaceholder: t("additionPlaceholder"),
                    street: t("streetLabel"),
                    city: t("cityLabel"),
                  }}
                  hints={{
                    lookup: t("lookupLoading"),
                    lookupNotFound: t("lookupNotFound"),
                  }}
                />
              </section>

              {/* Credentials */}
              <section className="space-y-4">
                <FormField
                  label={t("passwordField")}
                  name="password"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  onBlur={() => markTouched("password")}
                  required
                  autoComplete="new-password"
                  placeholder={t("minPasswordLength")}
                  error={errors.password}
                />
                <FormField
                  label={t("confirmPasswordField")}
                  name="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  onBlur={() => markTouched("confirmPassword")}
                  required
                  autoComplete="new-password"
                  placeholder={t("confirmPasswordPlaceholder")}
                  error={errors.confirmPassword}
                />
              </section>

              {/* Newsletter — one combined checkbox, default checked */}
              <label className="flex items-start gap-3 cursor-pointer p-4 bg-surface rounded-2xl">
                <input
                  type="checkbox"
                  checked={newsletterOptIn}
                  onChange={(e) => setNewsletterOptIn(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-foreground"
                />
                <div>
                  <span className="text-[14px] font-medium text-foreground block">
                    {t("newsletterLabel")}
                  </span>
                  <span className="text-[12px] text-text-tertiary block mt-0.5 leading-snug">
                    {t("newsletterHint")}
                  </span>
                </div>
              </label>

              {error && (
                <div className="p-3 bg-negative/5 border border-negative/20 text-sm text-negative rounded-xl">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full h-12 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 rounded-lg"
                style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                    </svg>
                    {t("pleaseWait")}
                  </>
                ) : (
                  t("register")
                )}
              </button>
            </form>

            <div className="mt-8 pt-8 border-t border-border text-center">
              <p className="text-sm text-text-secondary">
                {t("hasAccount")}{" "}
                <Link
                  href="/account/login"
                  className="text-foreground font-medium hover:underline"
                >
                  {t("login")}
                </Link>
              </p>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}

function SubmittedNotice({ email }: { email: string }) {
  const t = useTranslations("account");
  const { resendVerifyEmail } = useCustomerAuth();
  const [resendState, setResendState] = useState<"idle" | "sending" | "sent">("idle");

  const onResend = async () => {
    if (resendState === "sending") return;
    setResendState("sending");
    await resendVerifyEmail(email);
    setResendState("sent");
  };

  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          <div className="max-w-md mx-auto text-center">
            <h1 className="heading-md mb-4">{t("registerSubmittedTitle")}</h1>
            <p className="text-[14px] text-text-secondary mb-2">
              {t("registerSubmittedBody", { email })}
            </p>
            <p className="text-[12px] text-text-tertiary mb-8">
              {t("registerSubmittedHint")}
            </p>
            <button
              onClick={onResend}
              disabled={resendState !== "idle"}
              className="inline-flex items-center justify-center px-6 h-11 bg-foreground text-background text-[14px] font-medium rounded-xl hover:bg-foreground/85 disabled:opacity-60"
            >
              {resendState === "sent"
                ? t("verifyResendSent")
                : resendState === "sending"
                  ? t("verifyResending")
                  : t("verifyResendCta")}
            </button>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
